import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2,
  Send,
  Mail,
  Users,
  Sparkles,
  RefreshCcw,
  ShieldCheck,
  Eye,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SEC-001 fix — admin auth is now a long random token supplied via
// X-Admin-Token, matched against ADMIN_TOKEN env on the backend using
// constant-time comparison. The token never lives in the JS bundle;
// operator pastes it once into this form and it rests in localStorage.
export const ADMIN_TOKEN_KEY = 'tmj_admin_token';

/** Minimal admin console — token-header auth. */
export default function AdminPage() {
  const [adminToken, setAdminToken] = useState(
    () => localStorage.getItem(ADMIN_TOKEN_KEY) || ''
  );
  const [pendingToken, setPendingToken] = useState('');
  const [authed, setAuthed] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [busy, setBusy] = useState(null); // 'weekly'|'new_drop'|'broadcast'|null
  const [dryRun, setDryRun] = useState(true);
  const [dryRunEmail, setDryRunEmail] = useState(
    () => localStorage.getItem('tmj_admin_dryrun_email') || ''
  );
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const authHeaders = useMemo(
    () => ({ 'X-Admin-Token': adminToken }),
    [adminToken]
  );

  const loadPreview = async (tokenOverride) => {
    const tokenToUse = tokenOverride ?? adminToken;
    if (!tokenToUse) return;
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/admin/digest/preview`, {
        headers: { 'X-Admin-Token': tokenToUse },
      });
      setPreview(res.data);
      setAuthed(true);
      localStorage.setItem(ADMIN_TOKEN_KEY, tokenToUse);
    } catch (e) {
      setAuthed(false);
      const status = e?.response?.status;
      if (status === 401) {
        // Wipe the bad token so the sign-in gate re-prompts cleanly.
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setAdminToken('');
        toast.error('Invalid admin token. Check your ADMIN_TOKEN env value.');
      } else {
        toast.error('Failed to load preview — check backend logs.');
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (adminToken) loadPreview(adminToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignIn = (e) => {
    e.preventDefault();
    const clean = pendingToken.trim();
    if (!clean) return;
    setAdminToken(clean);
    loadPreview(clean);
  };

  const handleSignOut = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAdminToken('');
    setPendingToken('');
    setAuthed(false);
    setPreview(null);
  };

  const persistDryRunEmail = (val) => {
    setDryRunEmail(val);
    if (val) localStorage.setItem('tmj_admin_dryrun_email', val);
    else localStorage.removeItem('tmj_admin_dryrun_email');
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const dryRunEmailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(dryRunEmail);
  const dryRunReady = !dryRun || dryRunEmailValid;

  const runWeekly = async () => {
    if (!dryRunReady) {
      toast.error('Enter your dry-run email first.');
      return;
    }
    setBusy('weekly');
    try {
      const url = dryRun
        ? `${API}/admin/digest/dispatch?only_email=${encodeURIComponent(dryRunEmail)}`
        : `${API}/admin/digest/dispatch`;
      const res = await axios.post(url, {}, { headers: authHeaders });
      const r = res.data.result;
      toast.success(
        `Weekly digest: sent ${r.sent}/${r.recipients} (this_week=${r.this_week_count}, recent=${r.recent_count})`
      );
    } catch (e) {
      toast.error(`Weekly dispatch failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const runNewDrop = async ({ forceAll }) => {
    if (!dryRunReady) {
      toast.error('Enter your dry-run email first.');
      return;
    }
    setBusy('new_drop');
    try {
      const params = new URLSearchParams();
      if (dryRun) params.set('only_email', dryRunEmail);
      if (forceAll) params.set('force_all', 'true');
      const res = await axios.post(
        `${API}/admin/digest/new-releases?${params.toString()}`,
        {},
        { headers: authHeaders }
      );
      const r = res.data.result;
      if (!r.new_count) {
        toast.info('No un-alerted new releases found. Try "Force resend all".');
      } else {
        toast.success(
          `Fresh-drop alert: ${r.new_count} title(s) → ${r.sent}/${r.recipients} recipient(s)`
        );
      }
    } catch (e) {
      toast.error(`New-drop dispatch failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const runBroadcast = async () => {
    if (!subject.trim()) {
      toast.error('Enter a subject line before broadcasting.');
      return;
    }
    if (!dryRunReady) {
      toast.error('Enter your dry-run email first.');
      return;
    }
    setBusy('broadcast');
    try {
      const payload = {
        subject: subject.trim(),
        message: message.trim() || null,
        release_ids: selectedIds.length ? selectedIds : null,
        only_email: dryRun ? dryRunEmail : null,
      };
      const res = await axios.post(`${API}/admin/broadcast`, payload, {
        headers: authHeaders,
      });
      const r = res.data.result;
      toast.success(
        `Broadcast: sent ${r.sent}/${r.recipients} (this_week=${r.this_week_count}, recent=${r.recent_count})`
      );
    } catch (e) {
      toast.error(`Broadcast failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  // -------- Render: sign-in gate --------
  if (!authed) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <form
          onSubmit={handleSignIn}
          data-testid="admin-signin-form"
          className="w-full max-w-md bg-neutral-950 border border-white/10 rounded-2xl p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheck className="w-6 h-6 text-emerald-500" />
            <div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">
                Admin Console
              </div>
              <div className="text-xl font-extrabold tracking-tight">
                Subscriber Broadcasts
              </div>
            </div>
          </div>
          <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-2">
            Admin token
          </label>
          <input
            data-testid="admin-token-input"
            type="password"
            value={pendingToken}
            onChange={(e) => setPendingToken(e.target.value)}
            placeholder="Paste your ADMIN_TOKEN…"
            className="w-full bg-black border border-white/15 rounded-lg px-4 py-3 focus:outline-none focus:border-emerald-500 font-mono text-sm"
            autoFocus
          />
          <button
            data-testid="admin-signin-submit"
            type="submit"
            className="mt-5 w-full bg-white text-black font-bold py-3 rounded-full hover:bg-neutral-200 transition"
          >
            Unlock
          </button>
          <p className="mt-4 text-xs text-neutral-500 leading-relaxed">
            Uses the long random string configured as <code>ADMIN_TOKEN</code>{' '}
            on the backend. Kept only in this browser's localStorage — never
            shipped in the site bundle.
          </p>
        </form>
      </div>
    );
  }

  // -------- Render: dashboard --------
  const thisWeek = preview?.this_week || [];
  const recent = preview?.recent || [];
  const subscribers = preview?.subscriber_count ?? 0;

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10 sticky top-0 z-20 bg-black/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-500 font-bold">
              The Movie Junkie • Admin
            </div>
            <div className="text-lg font-extrabold tracking-tight">
              Subscriber Broadcasts
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-neutral-400">
              Token authenticated
            </span>
            <button
              data-testid="admin-signout-btn"
              onClick={handleSignOut}
              className="text-xs text-neutral-400 hover:text-white border border-white/15 px-3 py-1.5 rounded-full"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Stats */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            icon={<Users className="w-4 h-4" />}
            label="Subscribers"
            value={subscribers}
            testid="admin-stat-subscribers"
          />
          <StatCard
            icon={<Sparkles className="w-4 h-4" />}
            label="This Week"
            value={thisWeek.length}
            testid="admin-stat-this-week"
          />
          <StatCard
            icon={<Mail className="w-4 h-4" />}
            label="Recently Released"
            value={recent.length}
            testid="admin-stat-recent"
          />
        </section>

        {/* Dry-run switch + email */}
        <div
          data-testid="admin-dryrun-toggle"
          className="flex flex-col sm:flex-row sm:items-center gap-3 bg-neutral-950 border border-white/10 rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <input
              id="dryrun"
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              className="w-4 h-4 accent-emerald-500"
            />
            <label htmlFor="dryrun" className="text-sm cursor-pointer">
              <span className="font-semibold">Dry-run mode</span> — send test
              copy only to the address below.
            </label>
          </div>
          <input
            data-testid="admin-dryrun-email"
            type="email"
            value={dryRunEmail}
            onChange={(e) => persistDryRunEmail(e.target.value)}
            placeholder="you@example.com"
            className="flex-1 bg-black border border-white/15 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
          />
          <button
            data-testid="admin-refresh-preview"
            onClick={() => loadPreview()}
            className="text-xs text-neutral-400 hover:text-white flex items-center gap-1 whitespace-nowrap"
          >
            <RefreshCcw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {/* Quick actions */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ActionCard
            title="Weekly Digest"
            description="Send the Thursday-style digest right now — full This Week + Recently Released set to every subscriber."
            cta="Send weekly digest"
            testid="admin-run-weekly"
            busy={busy === 'weekly'}
            onClick={runWeekly}
          />
          <ActionCard
            title="Fresh Release Alert"
            description="Notify subscribers about titles that entered the ±7d window since the last dispatch. Idempotent — won't re-alert."
            cta="Send only new drops"
            testid="admin-run-new-drop"
            busy={busy === 'new_drop'}
            onClick={() => runNewDrop({ forceAll: false })}
            secondaryCta={{
              label: 'Force resend all',
              testid: 'admin-run-force-all',
              onClick: () => runNewDrop({ forceAll: true }),
            }}
          />
        </section>

        {/* Custom broadcast */}
        <section
          data-testid="admin-broadcast-panel"
          className="bg-neutral-950 border border-white/10 rounded-2xl p-6 space-y-4"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-500 font-bold">
              Custom Broadcast
            </div>
            <h2 className="text-2xl font-extrabold tracking-tight">
              Blast a release update to the list
            </h2>
            <p className="text-sm text-neutral-400 mt-1">
              Ships the branded email template. Leave the picker empty to send
              the full weekly slate, or select specific titles below.
            </p>
          </div>

          <div className="grid gap-3">
            <input
              data-testid="admin-broadcast-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject line (e.g. Just added: Wednesday S3 date drop)"
              className="w-full bg-black border border-white/15 rounded-lg px-4 py-3 focus:outline-none focus:border-emerald-500"
            />
            <textarea
              data-testid="admin-broadcast-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Short intro shown under the hero (optional)"
              rows={3}
              className="w-full bg-black border border-white/15 rounded-lg px-4 py-3 focus:outline-none focus:border-emerald-500 resize-none"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">
                Feature releases (optional)
              </div>
              {selectedIds.length > 0 && (
                <button
                  data-testid="admin-broadcast-clear"
                  onClick={() => setSelectedIds([])}
                  className="text-xs text-neutral-400 hover:text-white"
                >
                  Clear ({selectedIds.length})
                </button>
              )}
            </div>
            <ReleasePicker
              title="This Week"
              releases={thisWeek}
              selectedIds={selectedIds}
              toggleSelect={toggleSelect}
            />
            <ReleasePicker
              title="Recently Released"
              releases={recent}
              selectedIds={selectedIds}
              toggleSelect={toggleSelect}
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              data-testid="admin-broadcast-send"
              onClick={runBroadcast}
              disabled={busy === 'broadcast' || !subject.trim() || !dryRunReady}
              className="bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold px-6 py-3 rounded-full flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {busy === 'broadcast' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {dryRun ? 'Send test' : 'Blast to subscribers'}
            </button>
            <span className="text-xs text-neutral-500">
              {dryRun
                ? dryRunEmail
                  ? `Test → ${dryRunEmail}`
                  : 'Enter a dry-run email above'
                : `Real recipients: ${subscribers}`}
            </span>
          </div>
        </section>

        {/* Preview data */}
        <section className="bg-neutral-950 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Eye className="w-4 h-4 text-neutral-400" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-bold">
              What's queued
            </div>
          </div>
          {previewLoading ? (
            <div className="text-neutral-400 flex items-center gap-2 py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading preview…
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              <PreviewList
                heading={`This Week (${thisWeek.length})`}
                accent="text-emerald-400"
                items={thisWeek}
                testid="admin-preview-this-week"
              />
              <PreviewList
                heading={`Recently Released (${recent.length})`}
                accent="text-amber-400"
                items={recent}
                testid="admin-preview-recent"
              />
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-neutral-950 border border-white/10 rounded-xl p-4"
    >
      <div className="flex items-center gap-2 text-neutral-400 text-xs uppercase tracking-wider">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-3xl font-black tracking-tight">{value}</div>
    </div>
  );
}

function ActionCard({ title, description, cta, onClick, busy, testid, secondaryCta }) {
  return (
    <div className="bg-neutral-950 border border-white/10 rounded-2xl p-6 flex flex-col">
      <h3 className="text-lg font-extrabold tracking-tight">{title}</h3>
      <p className="text-sm text-neutral-400 mt-1 flex-1">{description}</p>
      <div className="flex flex-wrap gap-2 mt-4">
        <button
          data-testid={testid}
          onClick={onClick}
          disabled={busy}
          className="bg-white text-black font-bold px-5 py-2.5 rounded-full flex items-center gap-2 hover:bg-neutral-200 disabled:opacity-50 transition"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {cta}
        </button>
        {secondaryCta && (
          <button
            data-testid={secondaryCta.testid}
            onClick={secondaryCta.onClick}
            disabled={busy}
            className="border border-white/20 text-white font-semibold px-5 py-2.5 rounded-full hover:bg-white/5 disabled:opacity-50 transition"
          >
            {secondaryCta.label}
          </button>
        )}
      </div>
    </div>
  );
}

function ReleasePicker({ title, releases, selectedIds, toggleSelect }) {
  if (!releases.length) return null;
  return (
    <div className="mb-3">
      <div className="text-xs text-neutral-500 mb-1">{title}</div>
      <div className="flex flex-wrap gap-2">
        {releases.map((r) => {
          const active = selectedIds.includes(r.id);
          return (
            <button
              key={r.id}
              data-testid={`admin-pick-${r.id}`}
              onClick={() => toggleSelect(r.id)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                active
                  ? 'bg-emerald-500 border-emerald-400 text-black font-bold'
                  : 'border-white/15 text-neutral-300 hover:border-white/40'
              }`}
            >
              {r.title}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PreviewList({ heading, accent, items, testid }) {
  return (
    <div data-testid={testid}>
      <div className={`text-xs uppercase tracking-wider font-bold mb-3 ${accent}`}>
        {heading}
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-neutral-500">Nothing here yet.</div>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-center gap-3 text-sm bg-black border border-white/5 rounded-lg p-2"
            >
              {item.poster_url && (
                <img
                  src={item.poster_url}
                  alt=""
                  className="w-8 h-12 object-cover rounded"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{item.title}</div>
                <div className="text-[11px] text-neutral-500">
                  {item.platform} · {(item.release_date || '').slice(0, 10)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
